/**
 * PropertyRelationService
 *
 * Manages relation and multi-relation property types.
 * Handles creating, reading, updating, and deleting object-to-object relationships.
 *
 * Features:
 * - CRUD operations for relations
 * - Backlink queries (find objects that link to a target)
 * - Position management for multi-relations
 * - Circular dependency validation
 * - Cache invalidation when relations change
 * - Full Supabase sync integration
 */

import { db } from '../db/database';
import { eventBus } from '../events/EventBus';
import { restSelect, restInsert, restUpdate, restDelete } from '../lib/supabaseRest';
import type { PropertyRelation, PropertyRelationLocal, CreateRelationRequest } from '../types/advanced-properties';
import type { PKMObject } from '../models/types';

/**
 * Convert Supabase PropertyRelation (snake_case) to IndexedDB format (camelCase)
 */
function toLocalRelation(relation: PropertyRelation): PropertyRelationLocal {
  return {
    id: relation.id,
    propertyId: relation.property_id,
    sourceObjectId: relation.source_object_id,
    targetObjectId: relation.target_object_id,
    position: relation.position,
    createdAt: relation.created_at,
  };
}

/**
 * Convert IndexedDB PropertyRelationLocal (camelCase) to Supabase format (snake_case)
 */
function toSupabaseRelation(local: PropertyRelationLocal): PropertyRelation {
  return {
    id: local.id,
    property_id: local.propertyId,
    source_object_id: local.sourceObjectId,
    target_object_id: local.targetObjectId,
    position: local.position,
    created_at: local.createdAt,
  };
}

export class PropertyRelationService {
  /**
   * Create a new relation between objects
   */
  async createRelation(
    propertyId: string,
    sourceObjectId: string,
    targetObjectId: string,
    position: number = 0,
    skipCircularCheck: boolean = false
  ): Promise<PropertyRelation> {
    try {
      // Check if relation already exists - return it instead of throwing
      const existing = await db.propertyRelations
        .where({ propertyId, sourceObjectId, targetObjectId })
        .first();
      if (existing) {
        return existing as unknown as PropertyRelation;
      }

      // Validate no circular dependency (skip for backlinks - they're meant to be bidirectional)
      if (!skipCircularCheck) {
        const hasCircular = await this.checkCircularDependency(sourceObjectId, targetObjectId);
        if (hasCircular) {
          throw new Error('Circular dependency detected');
        }
      }

      const relation: PropertyRelation = {
        id: this.generateId(),
        property_id: propertyId,
        source_object_id: sourceObjectId,
        target_object_id: targetObjectId,
        position,
        created_at: new Date().toISOString(),
      };

      // Store in Supabase first
      const created = await restInsert<PropertyRelation>('property_relations', [relation]);

      // Store locally for offline access
      await db.propertyRelations.add({
        id: relation.id,
        propertyId,
        sourceObjectId,
        targetObjectId,
        position,
        createdAt: relation.created_at,
      });

      // Invalidate cache for computed properties that depend on this relation
      await this.invalidateRelatedCache(propertyId, sourceObjectId);

      // Emit event for UI updates
      eventBus.emit('relation:created', {
        propertyId,
        sourceObjectId,
        targetObjectId,
        relation: created[0],
      });

      return created[0];
    } catch (error) {
      console.error('[PropertyRelationService] Error creating relation:', error);
      throw error;
    }
  }

  /**
   * Get all relations for a property on a specific object
   */
  async getRelationsForProperty(
    propertyId: string,
    sourceObjectId: string
  ): Promise<PropertyRelation[]> {
    return await db.propertyRelations
      .where('[propertyId+sourceObjectId]')
      .equals([propertyId, sourceObjectId])
      .sortBy('position');
  }

  /**
   * Get related objects (follows the relation and returns the target objects)
   */
  async getRelatedObjects(
    propertyId: string,
    sourceObjectId: string
  ): Promise<PKMObject[]> {
    const relations = await this.getRelationsForProperty(propertyId, sourceObjectId);

    const relatedObjects: PKMObject[] = [];
    for (const relation of relations) {
      const obj = await db.objects.get(relation.targetObjectId);
      if (obj && !obj.deletedAt) {
        relatedObjects.push(obj);
      }
    }

    return relatedObjects;
  }

  /**
   * Get backlinks - objects that link TO this object
   */
  async getBacklinks(
    propertyId: string,
    targetObjectId: string
  ): Promise<Array<{ sourceObject: PKMObject; relation: PropertyRelation }>> {
    const relations = await db.propertyRelations
      .where('[propertyId+targetObjectId]')
      .equals([propertyId, targetObjectId])
      .toArray();

    const backlinks: Array<{ sourceObject: PKMObject; relation: PropertyRelation }> = [];
    for (const relation of relations) {
      const obj = await db.objects.get(relation.sourceObjectId);
      if (obj && !obj.deletedAt) {
        backlinks.push({ sourceObject: obj, relation });
      }
    }

    return backlinks;
  }

  /**
   * Delete a specific relation
   */
  async deleteRelation(relationId: string): Promise<void> {
    const relation = await db.propertyRelations.get(relationId);
    if (!relation) return;

    await db.propertyRelations.delete(relationId);

    // Emit event
    eventBus.emit('relation:deleted', {
      propertyId: relation.propertyId,
      sourceObjectId: relation.sourceObjectId,
      targetObjectId: relation.targetObjectId,
      relationId,
    });

    // TODO: Queue for sync when SyncEngine updated
    console.log('[PropertyRelationService] Relation deleted (sync pending):', relationId);
  }

  /**
   * Delete all relations for a property on an object
   */
  async deleteAllRelations(
    propertyId: string,
    sourceObjectId: string
  ): Promise<void> {
    const relations = await this.getRelationsForProperty(propertyId, sourceObjectId);

    for (const relation of relations) {
      await this.deleteRelation(relation.id);
    }
  }

  /**
   * Update relation positions (for reordering in multi-relation)
   */
  async updateRelationPositions(
    propertyId: string,
    sourceObjectId: string,
    orderedRelationIds: string[]
  ): Promise<void> {
    for (let i = 0; i < orderedRelationIds.length; i++) {
      await db.propertyRelations.update(orderedRelationIds[i], {
        position: i,
      });
    }

    // Emit event
    eventBus.emit('relation:reordered', {
      propertyId,
      sourceObjectId,
      orderedRelationIds,
    });

    // TODO: Queue for sync when SyncEngine updated
    console.log('[PropertyRelationService] Relations reordered (sync pending)');
  }

  /**
   * Check if a relation already exists
   */
  async relationExists(
    propertyId: string,
    sourceObjectId: string,
    targetObjectId: string
  ): Promise<boolean> {
    const count = await db.propertyRelations
      .where({
        propertyId,
        sourceObjectId,
        targetObjectId,
      })
      .count();

    return count > 0;
  }

  /**
   * Add a relation (checks for duplicates first)
   */
  async addRelation(
    propertyId: string,
    sourceObjectId: string,
    targetObjectId: string
  ): Promise<PropertyRelation | null> {
    // Check if relation already exists
    const exists = await this.relationExists(propertyId, sourceObjectId, targetObjectId);
    if (exists) {
      console.log('[PropertyRelationService] Relation already exists, skipping');
      return null;
    }

    // Get current max position
    const relations = await this.getRelationsForProperty(propertyId, sourceObjectId);
    const maxPosition = relations.length > 0
      ? Math.max(...relations.map(r => r.position))
      : -1;

    return await this.createRelation(
      propertyId,
      sourceObjectId,
      targetObjectId,
      maxPosition + 1
    );
  }

  /**
   * Remove a relation by target object ID
   */
  async removeRelation(
    propertyId: string,
    sourceObjectId: string,
    targetObjectId: string
  ): Promise<void> {
    const relations = await db.propertyRelations
      .where({
        propertyId,
        sourceObjectId,
        targetObjectId,
      })
      .toArray();

    for (const relation of relations) {
      await this.deleteRelation(relation.id);
    }
  }

  /**
   * Generate a unique ID for relations
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Check for circular dependencies using DFS
   * Returns true if adding source -> target would create a cycle
   */
  private async checkCircularDependency(
    sourceId: string,
    targetId: string
  ): Promise<boolean> {
    try {
      // If source === target, that's circular
      if (sourceId === targetId) {
        return true;
      }

      // Use DFS to check if there's a path from target back to source
      const visited = new Set<string>();
      const stack = [targetId];

      while (stack.length > 0) {
        const currentId = stack.pop()!;

        if (visited.has(currentId)) {
          continue;
        }

        visited.add(currentId);

        // If we reach the source, there's a cycle
        if (currentId === sourceId) {
          return true;
        }

        // Get all objects that current object links to
        const relations = await db.propertyRelations
          .where('sourceObjectId')
          .equals(currentId)
          .toArray();

        // Add targets to stack
        for (const rel of relations) {
          if (!visited.has(rel.targetObjectId)) {
            stack.push(rel.targetObjectId);
          }
        }
      }

      return false; // No cycle found
    } catch (error) {
      console.error('[PropertyRelationService] Error checking circular dependency:', error);
      // On error, assume no circular dependency (fail open for better UX)
      return false;
    }
  }

  /**
   * Invalidate computed property cache when relation changes
   * (Rollups that depend on this relation need recalculation)
   */
  private async invalidateRelatedCache(
    propertyId: string,
    objectId: string
  ): Promise<void> {
    try {
      // Mark cache as stale for rollups that use this relation
      await restUpdate(
        'property_computed_cache',
        { is_stale: true },
        {
          eq: {
            property_id: propertyId,
            object_id: objectId,
          },
        }
      );
    } catch (error) {
      console.error('[PropertyRelationService] Error invalidating cache:', error);
      // Don't throw - cache invalidation is best-effort
    }
  }
}

// Export singleton instance
export const propertyRelationService = new PropertyRelationService();
